/**
 * Coil component creation logic
 */
import { ICoil, IRatedCoil } from '../../../models';
import { DEFAULT_COEFFICIENTS, SectionCreationContext } from '../../../models/bot';
import { IBotSection } from '../../../models';
import { dispatchMutation, dispatchQuery } from '../../../utils/dispatch.utils';
import { coilApi } from '../../../services';
import { generateTimestamp } from '../../index';

/**
 * Creates a coil component for a section
 */
export async function createCoilComponent(
  botSection: IBotSection,
  sectionId: number,
  context: SectionCreationContext,
  modelData: any,
): Promise<void> {
  const { getOptions, onProgress } = context;

  if (!botSection.duty_id) {
    throw new Error('Coil duty_id is required');
  }

  const coilModel = modelData?.model;
  const sending_data: any = {
    ...modelData.defaults,
    type_id: botSection.type_id,
    duty_id: botSection.duty_id,
    qty_stacked: +modelData.input.qty_stacked,
    airflow_actual: +modelData.input.airflow_actual_total,
    air_stream_id: +modelData.input.air_stream_id,
    mfg_id: coilModel.mfg_id,
    circuiting_id: coilModel.circuiting ? 12 : undefined,
    bot: true,
    mounting_angle: 0,
    h_from_floor: 0,
    conn_hand_id: botSection.conn_hand_id,
    htfluid_id: botSection.htfluid_id,
  };
  if (botSection.type_id != 1) {
    sending_data.entering_superheat = modelData.input.entering_superheat;
    sending_data.steam_pressure = modelData.input.steam_pressure;
  }

  const new_coil: ICoil = await dispatchMutation(
    coilApi.endpoints.createCoil,
    {
      body: {
        section_id: sectionId,
      },
    },
    getOptions(),
  );
  const coil_params: any = {
    mfg: modelData.input.mfg,
    circuiting_id: +modelData.input.circuiting ? 12 : '',
    htfluid_id: botSection.htfluid_id,
  };
  const extra = [
    'altitude',
    'airflow_actual_total',
    'face_velocity_actual',
    'apd_max',
    'eadb_temp',
    'eawb_temp',
    'desired_ladb_temp',
    'desired_lawb_temp',
    'suction_temp',
    'entering_superheat',
    'evaporator_mode',
  ];
  Object.entries(modelData.input).forEach((el) => {
    if (
      el[1] &&
      el[1] != '--' &&
      !el[0].includes('id') &&
      !extra.find((key) => key === el[0])
    ) {
      coil_params[el[0]] = el[1];
    }
  });
  const conn_dia: { item: { name: string; id: number } }[] =
    await dispatchQuery(coilApi.endpoints.getConnectionDiameters, {
      type_id: botSection.type_id,
      mfg_id: modelData.input.mfg_id,
    });
  const fpis: { item: { value: string; id: number } }[] = await dispatchQuery(
    coilApi.endpoints.getCoilFpis,
    {
      type_id: botSection.type_id,
      mfg_id: modelData.input.mfg_id,
    },
  );
  const rated_coil_sending_data = {
    coil_params: JSON.stringify(coil_params),
    inlet_conn_dia_id: conn_dia?.find(
      (el) => +el.item.name == +modelData.model.inlet_conn_dia,
    )?.item.id,
    outlet_conn_dia_id: conn_dia.find(
      (el) => +el.item.name == +modelData.model.outlet_conn_dia,
    )?.item.id,
    fh: modelData.input.fh,
    fl: modelData.input.fl,
    qty_stacked: modelData.input.qty_stacked,
    rows_id: modelData.model.rows,
    circuiting_id: +modelData.input.circuiting ? 12 : undefined,
    fpi: modelData.model.fpi,
    fpi_id: fpis?.find((el) => +el.item.value === +modelData.model.fpi)?.item
      .id,
    rating_datetime: generateTimestamp(),
    mfg_id: modelData.input.mfg_id,
    type_id: botSection.type_id,
    duty_id: botSection.duty_id,
    conn_hand_id: botSection.conn_hand_id,
    htfluid_id: botSection.htfluid_id,
    casing_flange_top_id: modelData.input.casing_flange_top_id,
    casing_flange_btm_id: modelData.input.casing_flange_btm_id,
    casing_flange_header_side_id: modelData.input.casing_flange_header_side_id,
    casing_flange_return_side_id: modelData.input.casing_flange_return_side_id,
    casing_mtl_id: 264,
    bot: true,
    h_from_floor: 0,
    airflow_actual_total: modelData.input.airflow_actual_total,
    air_stream_id: modelData.input.air_stream_id,
    ...modelData.defaults,
  };
  Object.entries(modelData.model).forEach((el) => {
    if (el[1] && el[1] != '--') {
      sending_data[el[0]] = el[1];
      rated_coil_sending_data[el[0]] = el[1];
    }
  });
  if (modelData.input.type === 'steam') {
    delete rated_coil_sending_data.inlet_conn_dia;
    delete rated_coil_sending_data.outlet_conn_dia;
    delete rated_coil_sending_data.outlet_header_dia;
    delete rated_coil_sending_data.inlet_header_dia;
  }
  sending_data.id = new_coil.id;
  rated_coil_sending_data.id = new_coil.id;
 
  const rated_coil: IRatedCoil = await dispatchMutation<IRatedCoil>(
    coilApi.endpoints.updateRatingModeCoil,
    {
      body: rated_coil_sending_data,
    },
    getOptions(),
  );
  // Store coil_clear_ups_in_section in modelData so it can be used later in updateSectionLength
  if (modelData) {
    modelData.coil_clear_ups_in_section =
      rated_coil?.settings?.clear_ups_in_section;
  }
  botSection.coil_depth = rated_coil?.depth;
  await dispatchMutation(
    coilApi.endpoints.updateCoil,
    {
      body: sending_data,
    },
    getOptions(),
  );
  onProgress?.(DEFAULT_COEFFICIENTS.coil_create); // coil_create coefficient
}
